import React from 'react';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { apiService } from '../services/apiService';

interface QuestionsTabProps {
  activeTab?: 'candidates' | 'text' | 'questions';
  onTabChange?: (tab: 'candidates' | 'text' | 'questions') => void;
  selectedPosition?: {
    id: number;
    title: string;
  } | null;
}

function SwitchToggleItems({ isActive, children, onClick }: { 
  isActive: boolean; 
  children: React.ReactNode;
  onClick?: () => void;
}) {
  const colors = useBrandingColors();
  if (isActive) {
    return (
      <div
        className="bg-[#ffffff] box-border content-stretch flex flex-row gap-1.5 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-[20px] shadow-[0px_6px_10px_0px_rgba(27,28,29,0.06),0px_2px_4px_0px_rgba(27,28,29,0.02)] shrink-0 cursor-pointer"
        onClick={onClick}
      >
        <div className="css-cf07vu font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-left text-nowrap tracking-[-0.084px]" style={{ color: colors.primary }}>
          <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
            {children}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-[#f5f6f1] box-border content-stretch flex flex-row gap-1.5 items-center justify-center overflow-clip px-3 py-2.5 relative rounded-[20px] shrink-0 cursor-pointer"
      onClick={onClick}
    >
      <div className="css-z8d4os font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#868c98] text-[14px] text-left text-nowrap tracking-[-0.084px]">
        <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
          {children}
        </p>
      </div>
    </div>
  );
}


function ContentDivider() {
  return (
    <div
      className="box-border content-stretch flex flex-row gap-2.5 items-center justify-center px-0 py-[1.5px] relative shrink-0 w-full"
    >
      <div
        className="basis-0 bg-[#e2e4e9] grow h-px min-h-px min-w-px shrink-0"
      />
    </div>
  );
}

function QuestionRow({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div className="box-border content-stretch flex flex-row gap-6 items-center justify-start leading-[0] not-italic px-0 py-3 relative rounded-2xl shrink-0 text-[#000000] text-[14px] text-left tracking-[-0.084px] w-full">
      <div className="css-5cyu6a font-['Inter:Medium',_sans-serif] font-medium relative shrink-0 text-nowrap">
        <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
          {number}
        </p>
      </div>
      <div className="basis-0 css-5cyu6a font-['Inter:Regular',_sans-serif] font-normal grow min-h-px min-w-px relative shrink-0">
        <p className="block leading-[20px]">
          {children}
        </p>
      </div>
    </div>
  );
}

function QuestionsList({ selectedPosition, activeTab }: { 
  selectedPosition?: { id: number; title: string } | null;
  activeTab?: 'candidates' | 'text' | 'questions';
}) {
  const [questions, setQuestions] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Загружаем вопросы при изменении выбранной вакансии или переключении на вкладку вопросов
  React.useEffect(() => {
    console.log('🔍 QuestionsList useEffect:', { selectedPositionId: selectedPosition?.id, activeTab });
    
    if (!selectedPosition?.id || activeTab !== 'questions') {
      console.log('🔍 Early return:', { hasPosition: !!selectedPosition?.id, isQuestionsTab: activeTab === 'questions' });
      setQuestions([]);
      return;
    }

    const loadQuestions = async () => {
      setLoading(true);
      setError(null);
      
             try {
         console.log('🔍 Загружаем вопросы для вакансии:', selectedPosition.id);
         
         // Реальный API вызов для получения вопросов с настройками
         const response = await apiService.getPositionQuestionsWithSettings(selectedPosition.id);
         console.log('🔍 Ответ API с вопросами:', response);
         
         // Извлекаем вопросы из ответа API
         const questions = response.questions || [];
         const questionTexts = questions.map((q: any) => q.text);
         
         console.log('🔍 Извлеченные вопросы:', questionTexts);
         setQuestions(questionTexts);
      } catch (err) {
        console.error('Ошибка при загрузке вопросов:', err);
        setError('Не удалось загрузить вопросы');
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [selectedPosition?.id, activeTab]);

  if (loading) {
    return (
      <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-1 items-center justify-center px-6 py-12 relative w-full">
            <div className="css-z8d4os font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#868c98] text-[14px] text-left text-nowrap tracking-[-0.084px]">
              <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                Загрузка вопросов...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-1 items-center justify-center px-6 py-12 relative w-full">
            <div className="css-z8d4os font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#e16349] text-[14px] text-left text-nowrap tracking-[-0.084px]">
              <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                {error}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedPosition) {
    return (
      <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-1 items-center justify-center px-6 py-12 relative w-full">
            <div className="css-z8d4os font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#868c98] text-[14px] text-left text-nowrap tracking-[-0.084px]">
              <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                Выберите вакансию для просмотра вопросов
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full">
        <div className="relative size-full">
          <div className="box-border content-stretch flex flex-col gap-1 items-center justify-center px-6 py-12 relative w-full">
            <div className="css-z8d4os font-['Inter:Regular',_sans-serif] font-normal leading-[0] not-italic relative shrink-0 text-[#868c98] text-[14px] text-left text-nowrap tracking-[-0.084px]">
              <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                Для данной вакансии вопросы не настроены
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full">
      <div className="relative size-full">
        <div className="box-border content-stretch flex flex-col gap-1 items-start justify-start px-6 py-3 relative w-full">
          {questions.map((question, index) => (
            <React.Fragment key={index}>
              <QuestionRow number={index + 1}>
                {question}
              </QuestionRow>
              {index < questions.length - 1 && <ContentDivider />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export function QuestionsTab({ activeTab = 'questions', onTabChange, selectedPosition }: QuestionsTabProps) {
  console.log('🔍 QuestionsTab render:', { activeTab, selectedPosition });
  return <QuestionsList selectedPosition={selectedPosition} activeTab={activeTab} />;
} 